import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Grid, Snackbar, Typography } from '@mui/material';
import { userApi } from '@/services/user.api';
import { configurationApi } from '@/services/configuration.api';
import { boxApi } from '@/services/box.api';
import { LoadingState } from '@/components/common/LoadingState';
import { ErrorState } from '@/components/common/ErrorState';
import { ApartmentCard } from '@/components/configurations/ApartmentCard';
import { BoxEntryCard } from '@/components/box/BoxEntryCard';
import type { RealApartmentDetail } from '@/types';

/** Nome di default di una nuova configurazione (port di ConfigurationsPage.handleNew legacy). */
const NEW_CONFIGURATION_NAME = 'Nuova Configurazione';

/**
 * "I tuoi appartamenti": card appartamento (codice, tipologia, edificio/scala/piano/quadrante) con
 * le rispettive configurazioni. Consuma GET /me/real-apartments/detail (endpoint in arrivo).
 */
export default function ApartmentsPage() {
  const navigate = useNavigate();
  const [apartments, setApartments] = useState<RealApartmentDetail[] | null>(null);
  const [boxCount, setBoxCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const loadApartments = (): void => {
    setIsLoading(true);
    setError(null);
    userApi
      .getMyRealApartmentsDetail()
      .then((res) => {
        setApartments(res.realApartments);
        setIsLoading(false);
      })
      .catch(() => {
        setError('Impossibile caricare i tuoi appartamenti. Riprova.');
        setIsLoading(false);
      });
  };

  useEffect(() => {
    loadApartments();
    // La card "Box auto" compare solo se l'utente ha ≥1 box (fallimento silenzioso: sezione opzionale).
    boxApi
      .getMyBoxes()
      .then((res) => setBoxCount(res.boxes.length))
      .catch(() => setBoxCount(0));
  }, []);

  /** Crea una nuova configurazione per l'appartamento e apre il wizard (come il legacy handleNew). */
  const handleCreate = (realApartmentId: number): void => {
    configurationApi
      .create({ name: NEW_CONFIGURATION_NAME, realApartmentId })
      .then((row) => navigate(`/configurations/${row.id}`))
      .catch(() => setToast('Impossibile creare la configurazione. Riprova.'));
  };

  return (
    <Box sx={{ flex: 1, px: { xs: 2, md: 6 }, py: 5 }}>
      <Typography variant="h2" gutterBottom>
        I tuoi appartamenti
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Seleziona un appartamento per gestire le sue configurazioni.
      </Typography>

      {boxCount > 0 && (
        <Box sx={{ mb: 4 }}>
          <BoxEntryCard boxCount={boxCount} onOpen={() => navigate('/box')} />
        </Box>
      )}

      {isLoading && <LoadingState message="Caricamento appartamenti…" />}
      {error && <ErrorState message={error} onRetry={loadApartments} />}

      {!isLoading && !error && apartments && apartments.length === 0 && (
        <Typography variant="body1" color="text.secondary">
          Non risultano appartamenti associati al tuo account.
        </Typography>
      )}

      {!isLoading && !error && apartments && apartments.length > 0 && (
        <Grid container spacing={3}>
          {apartments.map((apartment) => (
            <Grid item xs={12} md={6} lg={4} key={apartment.id}>
              <ApartmentCard
                apartment={apartment}
                onOpenConfiguration={(id) => navigate(`/configurations/${id}`)}
                onCreateConfiguration={handleCreate}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Snackbar
        open={toast != null}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setToast(null)}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
